import React, { useEffect, useState } from 'react';
import {
  Col,
  Form,
  Row,
  VertButton,
  VertInput,
  VertModal,
} from '@vertem-core/vertice-ds';
import * as yup from 'yup';
import { isValid, parseISO } from 'date-fns';
import { NumericFormat } from 'react-number-format';
import { useHierarchyProductsLocalStorageContext } from 'Context/HierarchyLocalStorageContext';
import { notification } from 'antd';

const schema = yup.object().shape({
  quantity: yup
    .number()
    .required('Campo obrigatório')
    .min(1, 'Mínimo 1'),
});

const HierarchyProductEditionModal = ({ isOpen, onClose, skuProductToEdit, isEditModal, isRemoveModal }) => {
  const [form] = Form.useForm();
  const [valorProduto, setValorProduto] = useState(0);
  const [loading, setLoading] = useState(false);

  const { hierarchyProductsLocalStorageValue, setHierarchyLocalStorageValue } = useHierarchyProductsLocalStorageContext()

  const closeModal = () => {
    form.resetFields();
    form.setFieldsValue({
      nomeProduto: '',
      quantidade: '',
      valor: '',
      descricao: '',
    });

    onClose()
  };

  const removeProductFromLocalStorageBySku = (sku) => {
    try {
      setLoading(true);
      const newProducts = hierarchyProductsLocalStorageValue?.products?.filter((product) => product.sku !== sku);
      setHierarchyLocalStorageValue({ products: newProducts });
      setLoading(false);
      onClose();
    } catch (error) {
      notification.error({
        message: 'Erro ao remover produto',
      })
      setLoading(false);
    }
  };

  const updateProductFromLocalStorage = async (sku, data) => {
    try {
      setLoading(true);
      const products = hierarchyProductsLocalStorageValue?.products;
      const productToUpdate = await hierarchyProductsLocalStorageValue?.products?.find((product) => product.sku === sku);
      productToUpdate.quantity = data.quantity;
      setHierarchyLocalStorageValue({ products });
      setLoading(false);
      notification.success({
        message: 'Produto atualizado com sucesso',
      })
      onClose();
    } catch (error) {
      notification.error({
        message: 'Erro ao atualizar produto',
      })
      setLoading(false);
    }
  };

  function isValidDate(value) {
    try {
      const parsedDate = parseISO(value);
      return isValid(parsedDate);
    } catch (error) {
      return false;
    }
  }
  const yupSync = {
    async validator({ field }, value) {
      const isDateField = isValidDate(value);
      if (field === 'quantity') {
        if (value.includes('-') && !isDateField) {
          const sanitizedValue = value.replace(/[^\d]/g, '');

          await schema.validateSyncAt(field, { [field]: sanitizedValue });
        } else {
          await schema.validateSyncAt(field, { [field]: value })
        }
      }
    },
  };

  useEffect(() => {
    const products = hierarchyProductsLocalStorageValue?.products;

    const productToEdit = products && products.length && products?.find((product) => product.sku === skuProductToEdit);

    form.setFieldsValue({
      name: productToEdit?.name ?? '',
      quantity: productToEdit?.quantity.toString() ?? '',
      unitPrice: productToEdit?.unitPrice ?? 0,
      description: productToEdit?.description ?? '',
    })
    setValorProduto(productToEdit?.unitPrice ?? 0);
  }, [skuProductToEdit, hierarchyProductsLocalStorageValue]);

  return (
    <VertModal
      destroyOnClose
      centered
      open={isOpen}
      onCancel={loading ? () => { } : closeModal}
      onOk={closeModal}
      title={isEditModal ? 'Editar Produto' : 'Deseja realmente excluir?'}
      className="vert-modal-product-spot"
      footer={[
        <Row gutter={[16, 8]}>
          <Col lg={8} md={8} sm={8} xs={24}>
            <VertButton
              key="back"
              type="default"
              onClick={closeModal}
              disabled={loading}
              className='col-12'
            >
              Fechar
            </VertButton>
          </Col>

          <Col lg={16} md={16} sm={16} xs={24}>
            <VertButton
              key="submit"
              type={`${isEditModal ? 'highlight' : 'default'}`}
              htmlType="submit"
              loading={loading}
              className='col-12'
              form="vert-modal-product-spot__form"
            >
              {isEditModal ? 'Confirmar edição' : 'Remover produto'}
            </VertButton>
          </Col>
        </Row>,
      ]}
    >
      <Form
        preserve={false}
        onFinish={(values) => {
          if (isEditModal) {
            updateProductFromLocalStorage(skuProductToEdit, values);
          } else {
            removeProductFromLocalStorageBySku(skuProductToEdit);
          }
        }}
        form={form}
        id="vert-modal-product-spot__form"
      >
        <Row gutter={[16, 8]} className='margin-top-regular'>
          <Col lg={24} md={24} sm={24} xs={24}>
            <Form.Item name="name" rules={[yupSync]}>
              <VertInput disabled label="Nome do produto" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 8]}>
          <Col lg={12} md={12} sm={12} xs={24}>
            <Form.Item name="quantity" rules={[yupSync]}>
              <VertInput disabled={isRemoveModal} label="Quantidade" />
            </Form.Item>
          </Col>
          <Col lg={12} md={12} sm={12} xs={24}>
            <Form.Item name="unitPrice" rules={[yupSync]}>
              <div className="vert-input vert-input-number-spot">
                <label className="shrink-label">Valor unitário</label>
                <NumericFormat
                  disabled
                  name="unitPrice"
                  value={valorProduto}
                  prefix={'R$ '}
                  thousandSeparator="."
                  decimalSeparator=","
                  allowNegative={false}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  onValueChange={(values) => {
                    setValorProduto(values.floatValue);
                  }}
                />
              </div>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 8]}>
          <Col lg={24} md={24} sm={24} xs={24}>
            <Form.Item name="description" rules={[yupSync]}>
              <VertInput disabled label="Descrição do Produto" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </VertModal>
  );
};

export default HierarchyProductEditionModal;
